import { AnalyticsBrowser } from '@segment/analytics-next';

import { isAppOpenedInNativeAppBrowser } from '~/common/utils/app';
import { getBotTrafficObservationProperties, setAnalyticsUserId } from '~/utils/botTrafficSignals';
import { sentryMessage } from '~/utils/sentryHelpers';
import { EMPTY_WAF_TRAFFIC_METADATA, mergeWafTrafficMetadata } from '~/utils/wafSuspiciousTraffic';

import type { Analytics } from '@segment/analytics-next';
import type { WafTrafficMetadata } from '~/utils/wafSuspiciousTraffic';

type UtmParameters = {
  utm_campaign_lt: string;
  utm_content_lt: string;
  utm_medium_lt: string;
  utm_source_lt: string;
};

const CLIENT_IDENTIFY_TIMEOUT_MS = 2000;
const analyticsBrowser = new AnalyticsBrowser();
let analyticsInstance: Analytics | null = null;
let analyticsLoadPromise: Promise<Analytics> | null = null;

const SEGMENT_LOAD_OPTIONS = {
  retryQueue: true,
  integrations: {
    'Segment.io': {
      deliveryStrategy: {
        strategy: 'standard' as const,
        // Keep small events alive while the browser navigates away.
        config: { keepalive: true },
      },
    },
  },
};

export const loadAnalytics = (): Promise<Analytics> => {
  if (!analyticsLoadPromise) {
    analyticsBrowser.load(
      { writeKey: process.env.NEXT_PUBLIC_SEGMENT_WRITE_KEY || '' },
      SEGMENT_LOAD_OPTIONS
    );
    analyticsLoadPromise = analyticsBrowser.then(([instance]) => {
      analyticsInstance = instance;
      return instance;
    });
  }

  return analyticsLoadPromise;
};

const getAnalyticsClient = (): AnalyticsBrowser | Analytics =>
  analyticsInstance || analyticsBrowser;
let wafTrafficMetadata = { ...EMPTY_WAF_TRAFFIC_METADATA };

/**
 * Keeps the SSR WAF signal available to the client-side analytics helpers.
 * Once true, it remains true for the lifetime of the page so client-side
 * route changes cannot lose the original SSR request signal.
 */
export const setWafTrafficMetadata = (metadata: WafTrafficMetadata): void => {
  // Keep a true SSR decision for the current page session. Client-side route
  // transitions may not carry the original request header again.
  wafTrafficMetadata = mergeWafTrafficMetadata(wafTrafficMetadata, metadata);
};

export const getUtmParameters = (): UtmParameters => {
  const queryStringParams = new URLSearchParams(window.location.search);

  return {
    utm_campaign_lt: queryStringParams.get('utm_campaign_lt') ?? '',
    utm_content_lt: queryStringParams.get('utm_content_lt') ?? '',
    utm_medium_lt: queryStringParams.get('utm_medium_lt') ?? '',
    utm_source_lt: queryStringParams.get('utm_source_lt') ?? '',
  };
};

export const trackPage = (referrer?: string, pageUrl?: string): void => {
  if (typeof window === 'undefined') {
    return;
  }

  const isInApp = isAppOpenedInNativeAppBrowser();
  const pageReferrer = referrer ?? (typeof document === 'undefined' ? '' : document.referrer);

  // Keep the bot signals on the page event because the current issue is excess
  // page traffic. The same observation fields are also added to track events.
  const currentPageUrl = new URL(pageUrl || window.location.href, window.location.origin);
  const pageProperties = {
    is_from_native_app_browser: isInApp,
    path: currentPageUrl.pathname,
    search: currentPageUrl.search,
    url: currentPageUrl.href,
  } as Record<string, boolean | string>;

  if (pageReferrer) {
    pageProperties.referrer = pageReferrer;
  }
  void getAnalyticsClient()
    .page(undefined, undefined, {
      ...pageProperties,
      ...getBotTrafficObservationProperties(wafTrafficMetadata),
    })
    .catch(() => undefined);
};

export const getSegmentAnonymousId = (): string => {
  if (typeof window === 'undefined') {
    return '';
  }

  return analyticsInstance?.user().anonymousId() || '';
};

export const trackEvent = (
  eventName: string,
  properties?: Record<string, unknown>,
  includeUtmParameters = true
): void => {
  if (typeof window === 'undefined') {
    return;
  }

  const isInApp = isAppOpenedInNativeAppBrowser();

  const trackProperties = {
    ...properties,
    user_channel: 'website',
    is_from_native_app_browser: isInApp,
    ...wafTrafficMetadata,
  };

  if (includeUtmParameters) {
    Object.assign(trackProperties, getUtmParameters());
  }

  void getAnalyticsClient()
    .track(eventName, trackProperties)
    .catch(() => undefined);
};

/**
 * Clears Segment's current user identity so activity after logout is anonymous
 * and cannot be attributed to the account that just signed out.
 */
export const resetAnalytics = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  void Promise.resolve(getAnalyticsClient().reset()).catch(() => undefined);
};

/**
 * Explicitly links the current anonymous browser identity to a known user.
 * Identify updates Segment's known user profile, while some destinations,
 * including Mixpanel, also require Alias to merge the user's earlier
 * anonymous history into that profile.
 */
export const alias = (userId: number | string): void => {
  if (typeof window === 'undefined') {
    return;
  }

  // Analytics Next buffers alias calls until AnalyticsProvider loads the SDK.
  void getAnalyticsClient()
    .alias(String(userId))
    .catch(() => undefined);
};

export const identify = async (userId: number, traits: Record<string, unknown>): Promise<void> =>
  new Promise((resolve) => {
    try {
      setAnalyticsUserId(userId);

      // Analytics Next buffers identify calls until AnalyticsProvider loads the
      // SDK. AuthenticationProvider can run its effect before that happens.
      if (
        typeof window === 'undefined' ||
        (typeof navigator !== 'undefined' && navigator.webdriver === true)
      ) {
        console.warn('[identify] analytics not available');
        return resolve();
      }

      let finished = false;
      let timeoutId: ReturnType<typeof setTimeout> | null = null;

      const finish = (): void => {
        if (!finished) {
          finished = true;

          if (timeoutId) {
            clearTimeout(timeoutId);
          }

          resolve();
        }
      };

      // Safety timeout: ensures we don't hang forever if callback never fires
      // This might happen with certain ad blockers
      timeoutId = setTimeout(() => {
        sentryMessage('Segment [identify] timed out', { userId });
        finish();
      }, CLIENT_IDENTIFY_TIMEOUT_MS);

      void getAnalyticsClient()
        .identify(String(userId), traits)
        .then(finish)
        .catch((error: unknown) => {
          console.warn('[identify] analytics.identify failed', error);
          finish();
        });
    } catch (error) {
      console.error('[identify] failed:', error);
      resolve();
    }
  });
